/**
 * c-drag-scroll — drag a horizontal strip with the mouse.
 *
 * Opt in with `data-drag-scroll` on any element that scrolls horizontally.
 * Shared by c-protocol-picker, c-detail-tabs and c-journey-stages, whose
 * mobile nav strips are all `overflow-x: auto` rows of buttons.
 *
 * Touch is deliberately NOT handled: the browser already scrolls these
 * natively, with momentum and with the correct hand-off to vertical page
 * scrolling. Re-implementing that in JS loses both.
 *
 * The two things the existing rail in c-products-swiper-v2 gets wrong, and
 * why this file exists instead of a fourth copy of it:
 *
 *   1. It refuses to start a drag on a button, so on a strip that is
 *      entirely buttons there is nowhere left to grab.
 *   2. It never suppresses the click, so a drag that starts on a button
 *      activates that button on release.
 *
 * Both are fixed here with a movement threshold: under it the gesture is a
 * click and the button activates; over it the gesture is a drag and the
 * click is swallowed in the capture phase before it reaches the button.
 */
(function () {
  'use strict';

  var THRESHOLD = 5; // px of travel before a press becomes a drag

  function init(el) {
    if (el.dataset.dragScrollBound === 'true') return;
    el.dataset.dragScrollBound = 'true';

    var down = false;
    var dragged = false;
    var startX = 0;
    var startLeft = 0;
    var snap = '';

    function scrollable() {
      return el.scrollWidth > el.clientWidth + 1;
    }

    /* The grab cursor has to follow whether the strip actually overflows, not a
       breakpoint. Scoping it to a media query gets it wrong in both directions:
       a strip that still fits at a narrow width offers a grab that does nothing,
       and a strip that overflows at a wide width — which happens here, because
       the >=978px rules widen the tabs — offers no affordance at all while the
       last tab sits clipped off the edge. Only the element knows, so the element
       says so, and the sections style `.is-draggable`. */
    function syncAffordance() {
      el.classList.toggle('is-draggable', scrollable());
    }

    syncAffordance();
    if (window.ResizeObserver) {
      new ResizeObserver(syncAffordance).observe(el);
    } else {
      window.addEventListener('resize', syncAffordance);
    }
    // Web fonts land after first paint and change how wide the labels are.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(syncAffordance);
    }

    function end() {
      if (!down) return;
      down = false;
      el.classList.remove('is-dragging');
      // Restore snapping only after the browser has settled this frame,
      // so re-enabling it does not yank the strip to the nearest item
      // mid-gesture.
      window.requestAnimationFrame(function () {
        el.style.scrollSnapType = snap;
        el.style.scrollBehavior = '';
      });
    }

    el.addEventListener('pointerdown', function (event) {
      // Mouse only, primary button only. Touch and pen scroll natively.
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      if (!scrollable()) return;

      down = true;
      dragged = false;
      startX = event.clientX;
      startLeft = el.scrollLeft;

      // scroll-snap and smooth scrolling both fight a drag that writes
      // scrollLeft directly on every move.
      snap = el.style.scrollSnapType;
      el.style.scrollSnapType = 'none';
      el.style.scrollBehavior = 'auto';
    });

    el.addEventListener('pointermove', function (event) {
      if (!down) return;

      var deltaX = event.clientX - startX;

      if (!dragged && Math.abs(deltaX) > THRESHOLD) {
        dragged = true;
        el.classList.add('is-dragging');
        // Capture only once the gesture is really a drag, so a plain click
        // keeps its normal target and focus behaviour.
        if (el.setPointerCapture) {
          try {
            el.setPointerCapture(event.pointerId);
          } catch (e) {
            /* capture is an optimisation; the drag works without it */
          }
        }
      }

      if (!dragged) return;
      event.preventDefault();
      el.scrollLeft = startLeft - deltaX;
    });

    el.addEventListener('pointerup', end);
    el.addEventListener('pointercancel', end);
    el.addEventListener('lostpointercapture', end);
    el.addEventListener('mouseleave', end);

    // A drag that ends over a button must not activate it. Capture phase, so
    // this runs before the button's own listener.
    el.addEventListener(
      'click',
      function (event) {
        if (!dragged) return;
        dragged = false;
        event.preventDefault();
        event.stopPropagation();
      },
      true
    );

    // Dragging across labels would otherwise select their text.
    el.addEventListener('dragstart', function (event) {
      if (dragged) event.preventDefault();
    });
  }

  function initAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var strips = scope.querySelectorAll('[data-drag-scroll]');
    for (var i = 0; i < strips.length; i++) init(strips[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  // Sections re-rendered by the theme editor never fire DOMContentLoaded.
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target || document);
  });
})();
